/**
 * Chatbot Icons Component
 * Module-specific icon components for chatbot UI.
 */

import { memo } from 'react';

/**
 * Icon component props.
 */
interface IconProps {
  /** Icon size in pixels */
  size?: number;
  /** Additional CSS class name */
  className?: string;
}

/**
 * Bot avatar icon for chatbot header and messages.
 */
export const BotAvatarIcon = memo(({ size = 24, className }: IconProps) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <path
      d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM13 19H11V17H13V19ZM15.07 11.25L14.17 12.17C13.45 12.9 13 13.5 13 15H11V14.5C11 13.67 11.45 12.9 12.17 12.17L13.1 11.25C13.67 10.68 14 10.11 14 9.5C14 8.12 12.88 7 11.5 7C10.12 7 9 8.12 9 9.5H7C7 6.79 9.29 4.5 12 4.5C14.71 4.5 17 6.79 17 9.5C17 10.32 16.75 11.14 16.25 11.75L15.07 11.25Z"
      fill="currentColor"
    />
  </svg>
));

BotAvatarIcon.displayName = 'BotAvatarIcon';

/**
 * Send icon for message input button.
 */
export const SendIcon = memo(({ size = 20, className }: IconProps) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <path
      d="M2.01 21L23 12L2.01 3L2 10L17 12L2 14L2.01 21Z"
      fill="currentColor"
    />
  </svg>
));

SendIcon.displayName = 'SendIcon';

/**
 * Chat bubble icon for floating widget button (closed state).
 */
export const ChatBubbleIcon = memo(({ size = 24, className }: IconProps) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <path
      d="M20 2H4C2.9 2 2 2.9 2 4V22L6 18H20C21.1 18 22 17.1 22 16V4C22 2.9 21.1 2 20 2ZM20 16H6L4 18V4H20V16Z"
      fill="currentColor"
    />
    <path
      d="M7 9H17V11H7V9ZM7 12H14V14H7V12Z"
      fill="currentColor"
    />
  </svg>
));

ChatBubbleIcon.displayName = 'ChatBubbleIcon';

/**
 * Close icon for floating widget button (open state).
 */
export const CloseIcon = memo(({ size = 24, className }: IconProps) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <path
      d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z"
      fill="currentColor"
    />
  </svg>
));

CloseIcon.displayName = 'CloseIcon';

/**
 * User icon for user messages.
 */
export const UserIcon = memo(({ size = 20, className }: IconProps) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <path
      d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z"
      fill="currentColor"
    />
  </svg>
));

UserIcon.displayName = 'UserIcon';
